<template>
  <div class="section-legend-panel" :style="{ width: `${width}px` }">
    <div class="panel-fixed-header">
      <h3 class="panel-title">Document Sections</h3>
      <button 
        class="button is-small is-primary"
        @click="$emit('start-tour')"
        v-if="false"
        title="Start guided tour for entity assignments"
      >
        Start Tour
      </button>
    </div>
    <div class="panel-scrollable-content">
      <div 
        v-for="section in sections" 
        :key="section.id" 
        class="section-item"
        :class="{
          'section-active': highlightedSection === section.id,
          'has-entity': sectionEntityMapping[section.id],
          'is-processing': processingSections[section.id]
        }"
        :data-section="section.id"
      >
        <div class="section-fixed-header">
          <div class="section-title">
            {{ section.name }}
            <span v-if="sectionEntityMapping[section.id]" class="tag is-info is-light">
              {{ entities.find(e => e.id === sectionEntityMapping[section.id])?.name || 'Unknown' }}
            </span>
          </div>
          <div class="section-actions">
            <LoaderWithText v-if="processingSections[section.id]" size="small" />
            <button 
              v-else-if="sectionEntityMapping[section.id]"
              class="button is-small is-light ml-1"
              @click="handleUnlinkEntity(section.id)"
            >
              <span class="icon">
                <i class="fas fa-unlink"></i>
              </span>
            </button>
            <button 
              v-else
              class="button is-small is-light"
              @click="$emit('open-entity-dialog', section.id)"
            >
              <span class="icon">
                <i class="fas fa-link"></i>
              </span>
              <span>Link Entity</span>
            </button>
          </div>
        </div>
        
        <div v-if="processingSections[section.id]" class="section-loading-full">
          <LoaderWithText size="medium" message="Processing section..." />
        </div>
        
        <div v-else class="section-fields">
          <div 
            v-for="field in sectionFields[section.id]"
            :key="field.id"
            class="field-item"
            :class="{ 'has-value': field.value }"
            :data-field-id="field.id || field.name || field.context?.id"
          >
            <div class="field-content">
              <div class="field-header">
                <div class="field-name">{{ field.context.label || field.context.descriptive_key || file.name}}</div>
                <div class="field-actions">
                  <button 
                    v-if="field.sourceFields?.length"
                    @click="toggleFieldDetails(field)"
                    class="info-button"
                    :class="{ 'is-active': expandedFields[field.id] }"
                  >
                    <span class="icon is-small">
                      <i class="fas fa-info-circle"></i>
                    </span>
                  </button>
                  <button @click="$emit('navigate-to-source', field)">
                    <span class="icon is-small">
                      <i class="fas fa-search"></i>
                    </span>
                  </button>
                </div>
              </div>
              
              <div class="field-value">
                <template v-if="field.type === 'checkbox' || (field.type === 'Btn' && !(field.fieldFlags & 32768))">
                  <b-checkbox 
                    :value="store.fieldValues[field.id] ?? field.value ?? ''"
                    @input="updateFieldValue(field.id, $event)"
                    size="is-small"
                  >
                  </b-checkbox>
                </template>
                <template v-else-if="field.type === 'radio' || (field.type === 'Btn' && (field.fieldFlags & 32768))">
                  <b-radio
                    :value="store.fieldValues[field.id] ?? field.value ?? ''"
                    @input="updateFieldValue(field.id, $event)"
                    size="is-small"
                  >
                  </b-radio>
                </template>
                <template v-else>
                  <input
                    :value="store.fieldValues[field.id] ?? field.value ?? ''"
                    @input="updateFieldValue(field.id, $event.target.value)"
                    class="input is-small"
                    :placeholder="field.context.placeholder || 'Enter value'"
                  >
                </template>
              </div>
            </div>

            <div 
              v-if="field.sourceFields?.length && expandedFields[field.id]" 
              class="field-details"
            >
              <div v-if="field.transformation" class="detail-item">
                <strong>Transformation</strong>
                <div class="transformation-text">{{ field.transformation }}</div>
              </div>
              <div v-if="field.sourceFields?.length" class="detail-item">
                <strong>Source Fields</strong>
                <div v-for="(source, idx) in field.sourceFields" :key="idx" class="source-field">
                  <span class="source-field-key">{{ source.descriptive_key }}</span>
                  <span class="source-field-value">{{ source.value }}</span>
                </div>
              </div>
              <div v-if="field.confidence" class="detail-item">
                <strong>Confidence</strong>
                <span class="confidence-value">{{ (field.confidence * 100).toFixed(1) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFormDataStore } from '../store/formData';
import { LoaderWithText } from '@stakeui';

export default {
  name: 'SectionLegendPanel',
  
  components: {
    LoaderWithText,
  },

  data() {
    return {
      expandedFields: {},
      store: useFormDataStore(),
      fieldValues: {},
    };
  },

  props: {
    width: {
      type: Number,
      required: true
    },
  },

  computed: {
    sections() {
      if (!this.store.currentFormId) return [];
      return this.store.formSections;
    },

    sectionFields() {
      if (!this.store.currentFormId) return {};
      
      // Force reactivity by accessing reactive store state
      // These properties must be accessed for proper reactivity
      console.debug('Recomputing sectionFields with:', {
        mappingCount: Object.keys(this.store.sectionEntityMapping).length,
        processingCount: this.store.processingSections.length,
        valueCount: Object.keys(this.store.fieldValues).length
      });
      
      // Force reactivity by accessing the store's fields directly
      const fields = {};
      for (const section of this.sections) {
        // Access each field to ensure reactivity
        const sectionFields = this.store.sectionFields(section.id);
        fields[section.id] = sectionFields.map(field => {
          const metadata = this.store.fieldMetadata[field.id] || {};
          return {
            ...field,
            value: this.store.fieldValues[field.id] ?? field.value ?? '',
            sourceFields: metadata.sourceFields || field.sourceFields || [],
            transformation: metadata.transformation || field.transformation || '',
            confidence: metadata.confidence || field.confidence || 0,
          };
        });
      }
      return fields;
    },

    sectionEntityMapping() {
      // Make a shallow copy to ensure we're always getting the latest data
      return { ...this.store.sectionEntityMapping };
    },

    entities() {
      return this.store.filteredEntities;
    },

    processingSections() {
      // Convert the array to an object with section IDs as keys for easier lookup
      const processing = {};
      this.store.processingSections.forEach(sectionId => {
        processing[sectionId] = true;
      });
      return processing;
    },

    highlightedSection() {
      return this.store.highlightedSection;
    },
  },

  methods: {
    async handleUnlinkEntity(sectionId) {
      try {
        // First emit the unlink event and wait for it to complete
        await this.$emit('unlink-entity', sectionId);
        
        // The store's unlinkEntityFromSection now handles clearing field values
        // so we don't need to do it here
      } catch (error) {
        console.error('Error unlinking entity:', error);
        // You might want to show an error message to the user here
      }
    },

    findFieldById(fieldId) {
      // Helper method to find a field by ID across all sections
      for (const section of this.sections) {
        const fields = this.sectionFields[section.id] || [];
        const field = fields.find(f => f.id === fieldId);
        if (field) return field;
      }
      return null;
    },

    updateFieldValue(fieldId, value) {
      const field = this.findFieldById(fieldId);
      if (field) {
        console.log('SectionLegendPanel: Field value changed:', {
          fieldId,
          oldValue: field.value,
          newValue: value,
        });
        
        // Update store immediately
        this.store.setFieldValue({
          fieldId,
          value,
        });

        // Emit for any parent components
        this.$emit('update-field-value', { field, value });
      }
    },

    toggleFieldDetails(field) {
      this.$set(this.expandedFields, field.id, !this.expandedFields[field.id]);
    },

    scrollToField(fieldId) {
      // Find the field element
      const fieldElement = this.$el.querySelector(`[data-field-id="${fieldId}"]`);
      if (fieldElement) {
        // Scroll the field into view with smooth behavior
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Highlight the field briefly
        fieldElement.classList.add('highlight-field');
        setTimeout(() => {
          fieldElement.classList.remove('highlight-field');
        }, 2000);

        // Find and focus the input element
        const inputElement = fieldElement.querySelector('input, .b-checkbox input, .b-radio input');
        if (inputElement) {
          // Use setTimeout to ensure the scroll has completed
          setTimeout(() => {
            inputElement.focus();
          }, 100);
        }
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.section-legend-panel {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--stk-text-15);
  border-left: 1px solid var(--stk-border);
  position: relative;
  z-index: 0;

  .panel-fixed-header {
    flex: 0 0 auto;
    padding: 1rem;
    background: var(--stk-text-15);
    border-bottom: 1px solid var(--stk-border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 20;
  }

  .panel-title {
    color: var(--stk-text-strong);
    font-weight: 600;
    font-size: 1.25rem;
    margin: 0;
  }

  .panel-scrollable-content {
    flex: 1;
    overflow-y: auto;
    padding: 0;
    position: relative;
    height: 100%;
  }
}

.section-item {
  background: var(--stk-text-20);
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  margin: 1rem;
  position: relative;

  .section-fixed-header {
    background: var(--stk-text-20);
    padding: 0.75rem;
    border-bottom: 1px solid var(--stk-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .section-title {
    color: var(--stk-text-strong);
    font-weight: 600;
    font-size: 1rem;
  }

  .section-fields {
    padding: 0.75rem;
    background: var(--stk-text-15);
    position: relative;
    z-index: 1;
  }

  &.is-processing {
    opacity: 0.7;
    pointer-events: none;
    
    .section-fields {
      display: none;
    }
  }
}

.field-item {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
  z-index: 1;
  background: var(--stk-text-20);

  &:hover {
    border-color: var(--stk-primary);
    box-shadow: 0 2px 4px rgba(var(--stk-primary-rgb), 0.1);
    z-index: 2;
  }

  &.has-value {
    background: var(--stk-primary-10);
    border-left: 3px solid var(--stk-primary);
  }

  &.highlight-field {
    animation: highlightField 2s ease-out;
  }
}

@keyframes highlightField {
  0% {
    background-color: var(--stk-primary-20);
  }
  100% {
    background-color: var(--stk-text-20);
  }
}

.field-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
}

.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.field-name {
  font-weight: 500;
  color: var(--stk-text-strong);
  flex: 1;
  min-width: 0;
}

.field-value {
  width: 100%;
  display: flex;
  align-items: center;

  input {
    width: 100%;
    height: 32px;
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--stk-border);
    border-radius: var(--stk-border-radius);
    font-size: 0.9rem;
    background: var(--stk-text-15);
    color: var(--stk-text-strong);

    &:focus {
      border-color: var(--stk-primary);
      box-shadow: 0 0 0 2px rgba(var(--stk-primary-rgb), 0.2);
      outline: none;
    }

    &::placeholder {
      color: var(--stk-text-weak);
    }
  }
}

.field-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.5rem;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
    color: #4a90e2;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #4a90e2;
      color: white;
      border-color: #4a90e2;
    }

    .icon {
      font-size: 0.9rem;
    }
  }
}

.field-details {
  padding: 1rem;
  background: var(--stk-text-15);
  border-top: 1px solid var(--stk-border);
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--stk-text);
}

.detail-item {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: var(--stk-text-20);
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: var(--stk-text-strong);
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.source-field {
  padding: 0.5rem;
  margin-top: 0.5rem;
  background: var(--stk-text-15);
  border-radius: var(--stk-border-radius);
  color: var(--stk-text);
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:first-child {
    margin-top: 0;
  }

  .source-field-key {
    font-weight: 500;
    color: var(--stk-text-strong);
  }

  .source-field-value {
    color: var(--stk-text);
    margin-left: 1rem;
    word-break: break-word;
  }
}

.transformation-text {
  font-style: italic;
  color: var(--stk-text);
  line-height: 1.4;
}

.confidence-value {
  font-weight: 600;
  color: var(--stk-primary);
}

.info-button {
  &.is-active {
    background: #4a90e2;
    color: white;
    border-color: #4a90e2;
  }
}

.section-loading-full {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--stk-text-15);
  min-height: 100px;
  border-top: 1px solid var(--stk-border);
}

@media (max-width: 992px) {
  .section-legend-panel {
    width: 100% !important;
    margin-left: 0;
    margin-top: 1rem;
    max-height: 300px;
    z-index: 0;
  }
}
</style> 